import { PMTiles } from "pmtiles";
import { DemPack, getDemPacks, getDemPacksSync, resetDemPacks } from "./dem-packs";

type ArchiveVariant = "base" | "max";

interface DecodedTile {
  pixels: Uint8ClampedArray;
  size: number;
}

// Decoded RGBA is 1 MiB per 512 px tile. 32 tiles was smaller than one
// obstacle or approach refresh walks, so the LRU never hit and every refresh
// decoded its tiles again on the main thread. Bytes, not a count: the map
// page is where the iPad reaches its jetsam budget.
// 64 MiB is a middle value: a miss now decodes in the worker, off the main thread.
// ponytail: fixed byte cap; tune it with a device memory trace if needed.
const PIXEL_CACHE_MAX_BYTES = 64 * 1024 * 1024;
// Failed decodes are cached as null and cost no bytes; this bounds them.
const PIXEL_CACHE_MAX_ENTRIES = 1024;

const pixelCache = new Map<string, DecodedTile | null>();
const failedTileRetryAt = new Map<string, number>();
const FAILED_TILE_RETRY_MS = 5000;
let pixelCacheBytes = 0;

const tileBytes = (tile: DecodedTile | null | undefined): number => tile?.pixels.byteLength ?? 0;
const inflightTiles = new Map<string, Promise<DecodedTile | null>>();

export function resetPmTilesRegistry(): void {
  resetDemPacks();
  pixelCache.clear();
  failedTileRetryAt.clear();
  pixelCacheBytes = 0;
  inflightTiles.clear();
}

function lon2tileFrac(lon: number, z: number): number {
  return ((lon + 180) / 360) * Math.pow(2, z);
}

function lat2tileFrac(lat: number, z: number): number {
  const rad = (lat * Math.PI) / 180;
  return ((1 - Math.log(Math.tan(rad) + 1 / Math.cos(rad)) / Math.PI) / 2) * Math.pow(2, z);
}

function packCovers(pack: DemPack, lon: number, lat: number): boolean {
  const [minLon, minLat, maxLon, maxLat] = pack.manifest.bbox;
  return lon >= minLon && lon <= maxLon && lat >= minLat && lat <= maxLat;
}

// The decode and the pixel read cost about 2 s of main-thread time in one
// 35 s map profile, so a worker does them where it can. A Blob URL worker needs
// no bundler support. Colour conversion stays off: the pixels are elevations.
const DECODE_WORKER_SRC = `onmessage = async ({ data: { id, buffer } }) => {
  try {
    const bitmap = await createImageBitmap(new Blob([buffer], { type: "image/webp" }),
      { colorSpaceConversion: "none", premultiplyAlpha: "none" });
    const canvas = new OffscreenCanvas(bitmap.width, bitmap.height);
    const ctx = canvas.getContext("2d", { willReadFrequently: true });
    ctx.drawImage(bitmap, 0, 0);
    bitmap.close();
    const { data } = ctx.getImageData(0, 0, canvas.width, canvas.height);
    postMessage({ id, pixels: canvas.width ? data : null, size: canvas.width }, [data.buffer]);
  } catch {
    postMessage({ id, pixels: null, size: 0 });
  }
};`;

let decodeWorker: Worker | null | undefined;
let decodeSeq = 0;
const decodeWaiters = new Map<number, { resolve: (tile: DecodedTile | null) => void; posted: number }>();

function getDecodeWorker(): Worker | null {
  if (decodeWorker !== undefined) return decodeWorker;
  decodeWorker = null;
  if (typeof Worker !== "function" || typeof OffscreenCanvas !== "function") return null;
  try {
    const url = URL.createObjectURL(new Blob([DECODE_WORKER_SRC], { type: "text/javascript" }));
    decodeWorker = new Worker(url);
    URL.revokeObjectURL(url);
    decodeWorker.onmessage = ({ data }) => {
      decodeWaiters.get(data.id)?.resolve(data.pixels ? { pixels: data.pixels, size: data.size } : null);
      decodeWaiters.delete(data.id);
      // Ids rise and a Map keeps insertion order, so the first waiter is the oldest.
      // Answers for other tiles must not hide one decode that never answers.
      const oldest = decodeWaiters.values().next().value;
      if (oldest && Date.now() - oldest.posted > DECODE_REQUEST_MAX_MS) return disableDecodeWorker();
      // An answer is progress: wait another full period for the rest of the queue.
      clearTimeout(decodeWatchdog);
      decodeWatchdog = undefined;
      armDecodeWatchdog();
    };
    // A worker that fails to load or dies posts no message: fail its waiters over.
    decodeWorker.onerror = decodeWorker.onmessageerror = disableDecodeWorker;
  } catch {
    decodeWorker = null;
  }
  return decodeWorker;
}

async function decodeTilePixels(buffer: ArrayBuffer): Promise<DecodedTile | null> {
  const worker = getDecodeWorker();
  if (worker) {
    const id = ++decodeSeq;
    const tile = await new Promise<DecodedTile | null>((resolve) => {
      decodeWaiters.set(id, { resolve, posted: Date.now() });
      worker.postMessage({ id, buffer });
      armDecodeWatchdog();
    });
    if (tile) return tile;
    // Legacy Android WebView cannot decode VP8L through createImageBitmap.
    // One failure sends every later tile to the main-thread decoder.
    disableDecodeWorker();
  }
  return decodeTilePixelsOnMainThread(buffer);
}

// A decode takes milliseconds. No answer at all for this long, with requests
// waiting, means the worker is gone. It runs from the last answer, not from
// each request, so a long queue on a live worker does not trip it.
const DECODE_WORKER_TIMEOUT_MS = 5000;
// Upper bound for one request, queue time included.
const DECODE_REQUEST_MAX_MS = 30000;
let decodeWatchdog: ReturnType<typeof setTimeout> | undefined;

function armDecodeWatchdog(): void {
  if (decodeWatchdog === undefined && decodeWaiters.size > 0)
    decodeWatchdog = setTimeout(disableDecodeWorker, DECODE_WORKER_TIMEOUT_MS);
}

function disableDecodeWorker(): void {
  clearTimeout(decodeWatchdog);
  decodeWatchdog = undefined;
  decodeWorker?.terminate();
  decodeWorker = null;
  for (const waiter of decodeWaiters.values()) waiter.resolve(null);
  decodeWaiters.clear();
}

async function decodeTilePixelsOnMainThread(buffer: ArrayBuffer): Promise<DecodedTile | null> {
  // Decode via HTMLImageElement, not createImageBitmap(): Android System
  // WebView's createImageBitmap() cannot decode lossless (VP8L) WebP, which
  // is what the terrain-RGB DEM packs use (lossy WebP would corrupt the
  // elevation values). The <img> decoder handles VP8L on every platform.
  const url = URL.createObjectURL(new Blob([buffer], { type: "image/webp" }));
  try {
    const image = new Image();
    await new Promise<void>((resolve, reject) => {
      image.onload = () => resolve();
      image.onerror = () => reject(new Error("webp decode failed"));
      image.src = url;
    });
    const size = image.naturalWidth;
    if (size === 0) return null;
    const canvas = document.createElement("canvas");
    canvas.width = size;
    canvas.height = image.naturalHeight;
    const ctx = canvas.getContext("2d", { willReadFrequently: true });
    if (!ctx) return null;
    ctx.drawImage(image, 0, 0);
    const img = ctx.getImageData(0, 0, size, image.naturalHeight);
    return { pixels: img.data, size };
  } catch (err) {
    console.warn("pmtiles-elev: tile decode failed", err);
    return null;
  } finally {
    URL.revokeObjectURL(url);
  }
}

async function fetchAndDecode(
  archive: PMTiles,
  z: number,
  x: number,
  y: number,
  key: string
): Promise<DecodedTile | null> {
  let resp;
  try {
    resp = await archive.getZxy(z, x, y);
  } catch (err) {
    console.warn(`pmtiles-elev: getZxy failed for ${key}`, err);
    return null;
  }
  if (!resp) return null;
  return decodeTilePixels(resp.data);
}

export async function getTilePixels(
  pack: DemPack,
  variant: ArchiveVariant,
  z: number,
  x: number,
  y: number
): Promise<DecodedTile | null> {
  const archive = variant === "max" ? pack.archiveMax : pack.archive;
  if (!archive) return null;
  const key = `${pack.manifest.id}#${variant}/${z}/${x}/${y}`;
  let cached = pixelCache.get(key);
  // Missing tiles and transient decode/network failures must not poison later
  // endpoint lookups after the aircraft returns to covered terrain.
  if (cached === null && Date.now() >= (failedTileRetryAt.get(key) ?? 0)) {
    pixelCache.delete(key);
    failedTileRetryAt.delete(key);
    cached = undefined;
  }
  if (cached !== undefined) {
    // LRU: re-insert moves the key to the end of the Map's iteration
    // order, so eviction below always drops the least-recently-used tile.
    pixelCache.delete(key);
    pixelCache.set(key, cached);
    return cached;
  }

  const inflight = inflightTiles.get(key);
  if (inflight) return inflight;

  const task = fetchAndDecode(archive, z, x, y, key).then((result) => {
    pixelCacheBytes -= tileBytes(pixelCache.get(key));
    pixelCache.delete(key);
    while (
      pixelCache.size > 0 &&
      (pixelCache.size >= PIXEL_CACHE_MAX_ENTRIES || pixelCacheBytes + tileBytes(result) > PIXEL_CACHE_MAX_BYTES)
    ) {
      const firstKey = pixelCache.keys().next().value;
      pixelCacheBytes -= tileBytes(pixelCache.get(firstKey));
      pixelCache.delete(firstKey);
      failedTileRetryAt.delete(firstKey);
    }
    pixelCache.set(key, result);
    if (result === null) failedTileRetryAt.set(key, Date.now() + FAILED_TILE_RETRY_MS);
    else failedTileRetryAt.delete(key);
    pixelCacheBytes += tileBytes(result);
    inflightTiles.delete(key);
    return result;
  });
  inflightTiles.set(key, task);
  return task;
}

// Plausible elevation bounds in metres. Anything outside is treated as
// no-data — terrain-RGB encodes "no data" as (0,0,0) → -10 000 m, which
// would otherwise leak into the bilinear blend and produce huge negative
// outliers along coastlines / outside coverage regions.
const MIN_VALID_ELEV_M = -500;
const MAX_VALID_ELEV_M = 9000;

function decodeTerrainRgb(pixels: Uint8ClampedArray, size: number, px: number, py: number): number | null {
  const cx = Math.max(0, Math.min(size - 1, px));
  const cy = Math.max(0, Math.min(size - 1, py));
  const i = (cy * size + cx) * 4;
  const r = pixels[i];
  const g = pixels[i + 1];
  const b = pixels[i + 2];
  if (r === 0 && g === 0 && b === 0) return null;
  const elev = r * 65536 + g * 256 + b - 10000;
  if (elev < MIN_VALID_ELEV_M || elev > MAX_VALID_ELEV_M) return null;
  return elev;
}

/**
 * Look up terrain elevation in metres from the installed PMTiles DEM packs.
 *
 * Returns `null` when no covering pack is installed, the relevant tile is
 * missing inside the pack, or decode fails. Callers must fall back to
 * whatever legacy source they used before.
 *
 * An optional `signal` lets callers abort an in-flight lookup so the fetch
 * and WebP decode don't run to completion after the caller has lost
 * interest (e.g. seq invalidation in `refreshObstacles3d`). When aborted,
 * resolves to `null` rather than rejecting — callers want a "no elevation"
 * result, not an exception to handle.
 */
export async function pmtilesElevAt(lon: number, lat: number, signal?: AbortSignal): Promise<number | null> {
  if (signal?.aborted) return null;
  if (lat > 85.0511 || lat < -85.0511) return null;

  let packs: DemPack[];
  try {
    packs = await getDemPacks();
  } catch (err) {
    console.warn("pmtiles-elev: registry load failed", err);
    return null;
  }
  if (signal?.aborted) return null;
  if (packs.length === 0) return null;

  for (const pack of packs) {
    if (signal?.aborted) return null;
    if (!packCovers(pack, lon, lat)) continue;
    const z = pack.manifest.max_zoom;
    const xFrac = lon2tileFrac(lon, z);
    const yFrac = lat2tileFrac(lat, z);
    const tx = Math.floor(xFrac);
    const ty = Math.floor(yFrac);

    const tile = await getTilePixels(pack, "base", z, tx, ty);
    if (signal?.aborted) return null;
    if (!tile) continue;
    // No-data pixel: fall through to the next covering pack (e.g. a
    // regional hole backfilled by the Europe-wide pack) rather than
    // giving up on the first cover.
    const elev = elevAtFromTile(tile, xFrac, yFrac, tx, ty);
    if (elev != null) return elev;
  }

  return null;
}

/**
 * Max-elevation variant of `pmtilesElevAt`. Uses each pack's `archiveMax`
 * (built from `manifest.files.max`) at the resolution declared by
 * `manifest.max_pyramid.max_zoom` (falling back to the base `max_zoom`).
 * Returns `null` if no covering pack ships a max-elev archive.
 */
export async function pmtilesMaxElevAt(lon: number, lat: number): Promise<number | null> {
  if (lat > 85.0511 || lat < -85.0511) return null;

  let packs: DemPack[];
  try {
    packs = await getDemPacks();
  } catch (err) {
    console.warn("pmtiles-elev: registry load failed", err);
    return null;
  }
  if (packs.length === 0) return null;

  for (const pack of packs) {
    if (!pack.archiveMax) continue;
    if (!packCovers(pack, lon, lat)) continue;
    const z = pack.manifest.max_pyramid?.max_zoom ?? pack.manifest.max_zoom;
    const xFrac = lon2tileFrac(lon, z);
    const yFrac = lat2tileFrac(lat, z);
    const tx = Math.floor(xFrac);
    const ty = Math.floor(yFrac);

    const tile = await getTilePixels(pack, "max", z, tx, ty);
    if (!tile) continue;
    const elev = elevAtFromTile(tile, xFrac, yFrac, tx, ty);
    if (elev != null) return elev;
  }
  return null;
}

function elevAtFromTile(tile: DecodedTile, xFrac: number, yFrac: number, tx: number, ty: number): number | null {
  const { pixels, size } = tile;
  const u = (xFrac - tx) * (size - 1);
  const v = (yFrac - ty) * (size - 1);
  const u0 = Math.floor(u);
  const v0 = Math.floor(v);
  const u1 = Math.min(size - 1, u0 + 1);
  const v1 = Math.min(size - 1, v0 + 1);
  const fu = u - u0;
  const fv = v - v0;

  const e00 = decodeTerrainRgb(pixels, size, u0, v0);
  const e10 = decodeTerrainRgb(pixels, size, u1, v0);
  const e01 = decodeTerrainRgb(pixels, size, u0, v1);
  const e11 = decodeTerrainRgb(pixels, size, u1, v1);

  // Refuse to blend across no-data corners — otherwise a single
  // no-data neighbour drags the bilinear result toward zero.
  if (e00 == null || e10 == null || e01 == null || e11 == null) {
    return e00 ?? e10 ?? e01 ?? e11 ?? null;
  }

  const elev = e00 * (1 - fu) * (1 - fv) + e10 * fu * (1 - fv) + e01 * (1 - fu) * fv + e11 * fu * fv;
  return Math.round(elev);
}

/**
 * Synchronous variant of `pmtilesElevAt`: returns the elevation only if the
 * covering pack is loaded AND the relevant tile is already decoded in the
 * in-memory pixel cache. Returns `null` on any miss. Used by hot pan paths
 * to avoid a Promise microtask round-trip when the cache already has the
 * data (common during slow pans within already-visited terrain).
 */
export function pmtilesElevAtSync(lon: number, lat: number): number | null {
  if (lat > 85.0511 || lat < -85.0511) return null;
  const packs = getDemPacksSync();
  if (!packs || packs.length === 0) return null;

  for (const pack of packs) {
    if (!packCovers(pack, lon, lat)) continue;
    const z = pack.manifest.max_zoom;
    const xFrac = lon2tileFrac(lon, z);
    const yFrac = lat2tileFrac(lat, z);
    const tx = Math.floor(xFrac);
    const ty = Math.floor(yFrac);
    const key = `${pack.manifest.id}#base/${z}/${tx}/${ty}`;
    const tile = pixelCache.get(key);
    if (tile === undefined) return null;
    if (tile === null) continue;
    pixelCache.delete(key);
    pixelCache.set(key, tile);
    return elevAtFromTile(tile, xFrac, yFrac, tx, ty);
  }
  return null;
}
